import { videoApi } from '@/api/video.api';
import type { BlockData } from '@/types/eduflow';

export type VideoSource = 'upload' | 'youtube' | 'vimeo' | 'url';
export type VideoPlayerTheme = 'app' | 'cinema' | 'glass' | 'editorial';
export type VideoPlayerAccent = 'primary' | 'fuchsia' | 'cyan' | 'amber' | 'emerald';
export type VideoPlayerAspectRatio = '16:9' | '21:9' | '4:3' | '1:1';
export type VideoPlayerRadius = 'soft' | 'rounded' | 'square';
export type VideoPlayerShadow = 'soft' | 'glow' | 'dramatic';

export interface VideoBlockConfig {
  rawUrl: string;
  resolvedUrl: string;
  source: VideoSource;
  youtubeId: string | null;
  vimeoId: string | null;
  embedUrl: string;
  usesIframe: boolean;
  startTime: number;
  endTime?: number;
  autoplay: boolean;
  loop: boolean;
  muted: boolean;
  showControls: boolean;
  minWatchPercent: number;
  subtitleLanguage: string;
  playerTheme: VideoPlayerTheme;
  playerAccent: VideoPlayerAccent;
  playerAspectRatio: VideoPlayerAspectRatio;
  playerRadius: VideoPlayerRadius;
  playerShadow: VideoPlayerShadow;
  showPlayerHeader: boolean;
  uploadedVideoName?: string;
  uploadedVideoThumbnail?: string;
  uploadedVideoDuration?: number;
  uploadedVideoResolution?: string;
  /** Fichier WebVTT généré (transcription Mistral) pour sous-titres synchrones dans le player. */
  captionTrackUrl?: string;
}

export function extractYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([^&?\s]+)/);
  return match ? match[1] : null;
}

export function extractVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}

export function inferVideoSource(url: string, preferred?: string): VideoSource {
  if (preferred === 'upload' || preferred === 'youtube' || preferred === 'vimeo' || preferred === 'url') {
    return preferred;
  }

  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  if (url.startsWith('/uploads/')) return 'upload';
  return 'url';
}

export function getAspectRatioStyle(ratio: VideoPlayerAspectRatio): { aspectRatio: string } {
  switch (ratio) {
    case '21:9':
      return { aspectRatio: '21 / 9' };
    case '4:3':
      return { aspectRatio: '4 / 3' };
    case '1:1':
      return { aspectRatio: '1 / 1' };
    case '16:9':
    default:
      return { aspectRatio: '16 / 9' };
  }
}

/** Largeur / hauteur pour ce ratio (ex. 16:9 →16/9). Utile avec max-height pour garder le cadre au bon ratio. */
export function getAspectRatioWidthOverHeight(ratio: VideoPlayerAspectRatio): number {
  switch (ratio) {
    case '21:9':
      return 21 / 9;
    case '4:3':
      return 4 / 3;
    case '1:1':
      return 1;
    case '16:9':
    default:
      return 16 / 9;
  }
}

function buildYouTubeEmbedUrl(id: string, config: Pick<VideoBlockConfig, 'autoplay' | 'muted' | 'loop' | 'showControls' | 'startTime' | 'endTime'>): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    autoplay: config.autoplay ? '1' : '0',
    mute: config.muted ? '1' : '0',
    controls: config.showControls ? '1' : '0',
    start: String(Math.max(0, config.startTime)),
    /** Favorise l’affichage des sous-titres fournis par YouTube dans l’iframe. */
    cc_load_policy: '1',
  });

  if (config.loop) {
    params.set('loop', '1');
    params.set('playlist', id);
  }

  if (config.endTime) {
    params.set('end', String(config.endTime));
  }

  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

function buildVimeoEmbedUrl(
  id: string,
  config: Pick<VideoBlockConfig, 'autoplay' | 'muted' | 'loop' | 'showControls' | 'subtitleLanguage'>,
): string {
  const params = new URLSearchParams({
    autoplay: config.autoplay ? '1' : '0',
    muted: config.muted ? '1' : '0',
    loop: config.loop ? '1' : '0',
    title: '0',
    byline: '0',
    portrait: '0',
    controls: config.showControls ? '1' : '0',
  });
  const lang = config.subtitleLanguage?.split('-')[0];
  if (lang) params.set('texttrack', lang);

  return `https://player.vimeo.com/video/${id}?${params.toString()}`;
}

export function getVideoBlockConfig(data: BlockData): VideoBlockConfig {
  const settings = data.settings ?? {};
  /* `??` ne tombe pas sur videoUrl si mediaUrl est "" — les anciens blocs peuvent n’avoir que settings.videoUrl. */
  const topMedia = typeof data.mediaUrl === 'string' ? data.mediaUrl.trim() : '';
  const settingsVideo = typeof settings.videoUrl === 'string' ? settings.videoUrl.trim() : '';
  const settingsUrl = typeof settings.url === 'string' ? settings.url.trim() : '';
  const rawUrl = topMedia || settingsVideo || settingsUrl || '';

  const source = inferVideoSource(rawUrl, typeof settings.videoSource === 'string' ? settings.videoSource : typeof settings.source === 'string' ? settings.source : undefined);
  const youtubeId = extractYouTubeId(rawUrl);
  const vimeoId = extractVimeoId(rawUrl);
  const resolvedUrl = source === 'upload' || rawUrl.startsWith('/uploads/') ? videoApi.resolveUrl(rawUrl) : rawUrl;
  const startTime = Math.max(0, Number(settings.startTime ?? 0) || 0);
  const rawEndTime = Number(settings.endTime ?? 0) || 0;
  const endTime = rawEndTime > 0 ? rawEndTime : undefined;

  const config: VideoBlockConfig = {
    rawUrl,
    resolvedUrl,
    source,
    youtubeId,
    vimeoId,
    embedUrl: '',
    usesIframe: source === 'youtube' || source === 'vimeo',
    startTime,
    endTime,
    autoplay: Boolean(settings.autoplay ?? false),
    loop: Boolean(settings.loop ?? false),
    muted: Boolean(settings.muted ?? false),
    showControls: Boolean(settings.showControls ?? true),
    minWatchPercent: Math.min(100, Math.max(1, Number(settings.minWatchPercent ?? 80) || 80)),
    subtitleLanguage:
      typeof settings.subtitleLanguage === 'string'
        ? settings.subtitleLanguage
        : typeof settings.captionLanguage === 'string'
          ? settings.captionLanguage
          : 'fr',
    playerTheme: (settings.playerTheme as VideoPlayerTheme) ?? 'app',
    playerAccent: (settings.playerAccent as VideoPlayerAccent) ?? 'primary',
    playerAspectRatio: (settings.playerAspectRatio as VideoPlayerAspectRatio) ?? '16:9',
    playerRadius: (settings.playerRadius as VideoPlayerRadius) ?? 'rounded',
    playerShadow: (settings.playerShadow as VideoPlayerShadow) ?? 'soft',
    showPlayerHeader: Boolean(settings.showPlayerHeader ?? true),
    uploadedVideoName: typeof settings.uploadedVideoName === 'string' ? settings.uploadedVideoName : undefined,
    uploadedVideoThumbnail: typeof settings.uploadedVideoThumbnail === 'string' ? settings.uploadedVideoThumbnail : undefined,
    uploadedVideoDuration: typeof settings.uploadedVideoDuration === 'number' ? settings.uploadedVideoDuration : undefined,
    uploadedVideoResolution: typeof settings.uploadedVideoResolution === 'string' ? settings.uploadedVideoResolution : undefined,
    captionTrackUrl:
      typeof settings.captionTrackUrl === 'string' && settings.captionTrackUrl.trim()
        ? settings.captionTrackUrl.trim()
        : undefined,
  };

  if (config.source === 'youtube' && youtubeId) {
    config.embedUrl = buildYouTubeEmbedUrl(youtubeId, config);
  } else if (config.source === 'vimeo' && vimeoId) {
    config.embedUrl = buildVimeoEmbedUrl(vimeoId, config);
  } else {
    config.embedUrl = config.resolvedUrl;
  }

  return config;
}

/** Affichage léger (page « Le Bref ») : mêmes heuristiques YouTube / Vimeo que le bloc vidéo du designer. */
export function getLeBrefVideoPresentation(url: string): {
  mode: 'youtube' | 'vimeo' | 'native';
  embedSrc: string;
  nativeSrc: string;
} {
  const resolved = url.startsWith('/uploads/') ? videoApi.resolveUrl(url) : url;
  const youtubeId = extractYouTubeId(resolved);
  if (youtubeId) {
    return {
      mode: 'youtube',
      embedSrc: `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&playsinline=1&controls=1`,
      nativeSrc: resolved,
    };
  }
  const vimeoId = extractVimeoId(resolved);
  if (vimeoId) {
    return {
      mode: 'vimeo',
      embedSrc: `https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&controls=1`,
      nativeSrc: resolved,
    };
  }
  return { mode: 'native', embedSrc: '', nativeSrc: resolved };
}
